import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Building2,
  Search,
  CreditCard,
  RefreshCw,
  ChevronLeft,
  AlertCircle,
  Users,
  Clock,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import BillingAuditLog from '../components/billing/BillingAuditLog';
import { API_BASE_URL } from '../config/api';

const BACKEND_URL = API_BASE_URL;

const AdminBillingAuditPage = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(orgId || '');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  
  // Verify admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [user, navigate]);
  
  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (planFilter !== 'all') params.append('plan_tier', planFilter);
      params.append('limit', '100');
      
      const response = await fetch(
        `${BACKEND_URL}/api/admin/billing/organizations?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch organizations');
      
      const data = await response.json();
      setOrganizations(data.organizations || []);
      
      // If we have an org from URL, find and select it
      if (orgId && data.organizations) {
        const org = data.organizations.find(o => o.organization_id === orgId);
        if (org) {
          setSelectedOrg(org);
          setSelectedOrgId(orgId);
        }
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, planFilter, orgId]);
  
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);
  
  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
    setSelectedOrgId(org.organization_id);
    // Update URL
    navigate(`/admin/billing/${org.organization_id}`, { replace: true });
  };
  
  const handleBack = () => {
    setSelectedOrg(null);
    setSelectedOrgId('');
    navigate('/admin/billing', { replace: true });
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      active_paid: { bg: 'bg-green-500/10', text: 'text-green-500', label: 'Active Paid' },
      active_free: { bg: 'bg-blue-500/10', text: 'text-blue-500', label: 'Free' },
      past_due: { bg: 'bg-red-500/10', text: 'text-red-500', label: 'Past Due' },
      trialing: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', label: 'Trial' },
      canceled: { bg: 'bg-gray-500/10', text: 'text-gray-500', label: 'Canceled' },
    };
    const style = styles[status] || styles.active_free;
    return (
      <Badge className={cn(style.bg, style.text)}>
        {style.label}
      </Badge>
    );
  };
  
  if (user?.role !== 'admin') {
    return null;
  }
  
  return (
    <div className={cn("min-h-screen", colors.pageBg)}>
      <Sidebar />
      <div className="ml-16 lg:ml-56">
        <Header />
        <main className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              {selectedOrg && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleBack}
                  className={colors.textMuted}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              <div>
                <h1 className={cn("text-2xl font-bold", colors.textPrimary)}>
                  {selectedOrg ? `Billing Audit: ${selectedOrg.name}` : 'Admin Billing Audit'}
                </h1>
                <p className={cn("text-sm", colors.textMuted)}>
                  {selectedOrg 
                    ? 'View all billing events for this organization'
                    : 'View billing history for any organization'
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Organization Selection View */}
          {!selectedOrg ? (
            <div className="space-y-6">
              {/* Search and Filters */}
              <Card className={cn("p-4", colors.cardBg)}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
                    <Input
                      placeholder="Search organizations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={cn("pl-9", colors.inputBg)}
                    />
                  </div>
                  <Select value={planFilter} onValueChange={setPlanFilter}>
                    <SelectTrigger className={cn("w-[160px]", colors.inputBg)}>
                      <SelectValue placeholder="Plan Tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Plans</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="free">Free</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    onClick={fetchOrganizations}
                    disabled={loading}
                  >
                    <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
                  </Button>
                </div>
              </Card>
              
              {/* Organizations List */}
              <Card className={cn("p-0 overflow-hidden", colors.cardBg)}>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                    <span className={colors.textMuted}>Loading organizations...</span>
                  </div>
                ) : organizations.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className={cn("w-12 h-12 mx-auto mb-4 opacity-50", colors.textMuted)} />
                    <p className={colors.textMuted}>No organizations found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {organizations.map((org) => (
                      <div
                        key={org.organization_id}
                        className={cn(
                          "flex items-center justify-between p-4 cursor-pointer transition-colors hover:bg-white/5",
                          colors.cardBorder
                        )}
                        onClick={() => handleOrgSelect(org)}
                      >
                        <div className="flex items-center gap-4">
                          <div className={cn("p-2 rounded-lg", colors.pageBg)}>
                            <Building2 className={cn("w-5 h-5", colors.textAccent)} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={cn("font-medium", colors.textPrimary)}>
                                {org.name}
                              </span>
                              {getStatusBadge(org.subscription_status)}
                            </div>
                            <p className={cn("text-sm", colors.textMuted)}>
                              {org.organization_id}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4 text-green-500" />
                              <span className={cn("text-sm font-medium", colors.textPrimary)}>
                                {org.plan_tier === 'paid' ? 'Paid' : 'Free'}
                              </span>
                            </div>
                            {org.stripe_customer_id && (
                              <p className={cn("text-xs", colors.textMuted)}>
                                Stripe: {org.stripe_customer_id.slice(0, 12)}...
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4 text-blue-500" />
                              <span className={cn("text-xs", colors.textMuted)}>
                                {org.created_at 
                                  ? new Date(org.created_at).toLocaleDateString() 
                                  : 'N/A'
                                }
                              </span>
                            </div>
                          </div>
                          <CreditCard className={cn("w-5 h-5", colors.textMuted)} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          ) : (
            /* Billing Audit Log for Selected Organization */
            <BillingAuditLog 
              organizationId={selectedOrgId} 
              isAdmin={true}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminBillingAuditPage;
